import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { v7 as uuidv7 } from 'uuid'
import type { CreatedEntity } from '@/components/editor/editorActions'
import { buildPlainTextEditorStateJSON } from '@/components/editor/TiptapRichTextEditor'
import { useTaskContentMutations, useTaskLabelMutations, useTaskMutations } from '@/lib/db/rxdb/hooks'
import { createTaskWithLabels } from '@/lib/sync/taskActions'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { generateInitialRank } from '@/lib/utils/lexorank'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import useLoginStore from '@/store/LoginStore'

/**
 * Create a task from editor text ("Create task" / "/task", design 7a–7b).
 * The task lands in `projectId` (null = Inbox), assigned to the current user,
 * in the workspace's backlog status — the same defaults as quick-create.
 */
export function useEditorTaskCreator(projectId: string | null) {
  const { t } = useTranslation('tasks')
  const workspaceId = useLoginStore((s) => s.workspaceId)
  const userId = useLoginStore((s) => s.userId)
  const { createTask } = useTaskMutations()
  const { createTaskContent } = useTaskContentMutations()
  const { createTaskLabel } = useTaskLabelMutations()
  const { open } = useStatuses()

  return useCallback(
    async (name: string): Promise<CreatedEntity | null> => {
      const title = name.trim().slice(0, 500)
      const statusId =
        open.statuses.find((s) => s.progressType === PROGRESS_TYPES.BACKLOG)?.id ?? open.statuses[0]?.id
      if (!title || !workspaceId || !userId || !statusId) return null

      const id = uuidv7()
      const description = buildPlainTextEditorStateJSON('')
      try {
        await createTaskWithLabels({
          task: {
            id,
            name: title,
            description,
            descriptionFormat: 'tiptap',
            descriptionLength: description.length,
            descriptionPlainText: null,
            sortOrder: generateInitialRank(),
            projectId,
            userId,
            startDate: null,
            startDateType: null,
            dueDate: null,
            dueDateType: null,
            priority: 0,
            isSomeday: false,
            isAllDay: true,
            statusId,
            workItemTypeId: null,
            parentId: null,
            statusName: '',
            statusColor: '#gray',
          },
          labelIds: [],
          createTask,
          createTaskLabel,
          createTaskContent,
        })
        return { id, label: title }
      } catch {
        toast.error(t('editorTask.createFailed'))
        return null
      }
    },
    [open.statuses, workspaceId, userId, projectId, createTask, createTaskLabel, createTaskContent, t],
  )
}
